import React, { useState } from 'react';
import Breadcrumbs from '../../components/BreadCrumbs';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import Table from '../../components/tables/Table';
import Button from '../../components/buttons/Button';
import { CustomCard } from '../../components/cards/CustomCard';
import SearchBar from '../../components/searchBar/SearchBar';
import ShowModal from '../../utils/ShowModal';
import { AlertCircleIcon, BoxesIcon, CrossIcon, Download, History, Package2Icon, PenToolIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getKitAssemblyView, getKitInventoryView } from '../../services/inventory';
import { toast } from 'react-toastify';
import { useEffect } from 'react';
import Pagination from '../../components/tables/pagination/Pagination';
import KitAssemblyDetailedView from './KitAssemblyDetailedView';
import CustomLoading from '../../components/customLoading/CustomLoading';
import DateRangePicker from '../../components/inputFields/DateRangePicker';
import { BiRefresh } from 'react-icons/bi';
import { CgClose } from 'react-icons/cg';
import Card from '../../components/cards/Card';
import { BASE_URL } from '../../constants/apis';

const KitAssembly = ({ forProduct = false }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showKitDetailsModal, setShowKitDetailsModal] = useState(false);
    const [selectedKit, setSelectedKit] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [exportReport, setExportReport] = useState(false);

    const [statsCardsData, setStatsCardsData] = useState([]);


    const handleDateRangeChange = (range) => {
        setDateRange(range);
        fetchData({ query: `start_date=${range.start}&end_date=${range.end}` });
    };

    // Predefined kit templates
    const [kitTemplates, setKitTemplates] = useState({});

    // Recent assembly activities
    const [assemblyHistory, setAssemblyHistory] = useState([]);

    const getStatusBadge = (status, forDropDown = false) => {
        const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
        switch (status) {
            case 'active':
                return forDropDown ? "#00ff00" : `${baseClasses} bg-green-100 text-green-800`;
            case 'inactive':
                return forDropDown ? "#ff0000" : `${baseClasses} bg-gray-100 text-gray-800`;
            case 'discontinued':
                return forDropDown ? "#00f0f0" : `${baseClasses} bg-blue-100 text-blue-800`;
        }
    };

    const openKitDetailsModal = (kit) => {
        setSelectedKit(kit);
        setShowKitDetailsModal(true);
    };

    useEffect(() => {
        fetchData({ query: "" });
        fetchAssemblyHistoryData({ query: "" });
    }, [forProduct]);

    const fetchData = async ({ query = "" }) => {
        try {
            setLoading(true);
            const res = await getKitInventoryView(`?${query}&inventory_type=${forProduct ? "purchased" : "self_made"}`);
            if (res.status === 200) {
                setKitTemplates(res.data);
                setStatsCardsData([
                    {
                        title: "Total Kits",
                        count: res.data.stats[0].value,
                        icon: BoxesIcon,
                        bgGradient: "from-blue-50 to-blue-100",
                        textColor: "text-blue-900"
                    },
                    {
                        title: "Assembled Kits (Last Week)",
                        count: res.data.stats[1].value,
                        icon: Package2Icon,
                        bgGradient: "from-green-50 to-green-100",
                        textColor: "text-green-900"
                    },
                    {
                        title: "Low Stock Alerts",
                        count: res.data.stats[2].value,
                        icon: AlertCircleIcon,
                        bgGradient: "from-red-50 to-red-100",
                        textColor: "text-red-900"
                    },
                ]);
            } else {
                toast.error(res.data);
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            toast.error("something went wrong");
        }
    };

    const fetchAssemblyHistoryData = async ({ query = "" }) => {
        const res = await getKitAssemblyView(`${query}`);
        if (res.status === 200) {
            setAssemblyHistory(res.data);
        } else {
            toast.error(res.data);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e);
        fetchData({ query: `search=${e}` });
    };

    const resetFilters = () => {
        setSearchTerm("");
        fetchData("");
    };

    const handleExportReport = () => {
        const inventoryType = forProduct ? "purchased" : "self_made";
        window.open(
            `${BASE_URL}/inventory/kit-download-excel?start_date=${dateRange.start}&end_date=${dateRange.end}&inventory_type=${inventoryType}`,
            "_blank"
        );
    };

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Inventory' },
                { name: forProduct ? 'Product Stock' : 'Kit Assembly' },
            ]} />

            <div className="grid grid-cols-4 gap-4 my-4">
                {statsCardsData.map((card, index) => (
                    <Card
                        key={index}
                        title={card.title}
                        count={card.count}
                        icon={card.icon}
                        bgGradient={card.bgGradient}
                        textColor={card.textColor}
                    />
                ))}
            </div>

            <div className="flex justify-between items-center mb-6">
                <StyledHeadingText text={forProduct ? 'Product Stock' : 'Kit Assembly System'} size={"text-3xl"} />
                <div className="flex gap-2">
                    {
                        exportReport ?
                            <div className="flex flex-row gap-2 items-center">
                                <DateRangePicker
                                    value={dateRange}
                                    onChange={handleDateRangeChange}
                                    className="w-full text-sm"
                                    compact
                                />
                                <Button
                                    className="bg-blue-400 hover:bg-primary-dark text-white px-2 py-2 rounded-lg flex items-center"
                                    onClick={handleExportReport}
                                >
                                    <Download size={16} />
                                </Button>
                                <Button
                                    className="bg-red-600 hover:bg-primary-dark text-white px-2 py-2 rounded-lg flex items-center"
                                    onClick={() => setExportReport(!exportReport)}
                                >
                                    <CgClose size={16} />
                                </Button>
                            </div> :
                            <Button
                                className="bg-blue-400 hover:bg-primary-dark text-white px-4 py-2 rounded-lg flex items-center"
                                onClick={() => setExportReport(!exportReport)}
                            >
                                <Download size={16} className="mr-2" />
                                Export Report
                            </Button>}
                    <Link
                        className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg"
                        to={forProduct ? "/inventory/product-stock/add" : "/inventory/kit-assembly/add"}
                    >
                        {forProduct ? 'Create New Product Stock' : 'Create New Kit Template'}
                    </Link>
                </div>
            </div>

            {/* Search Section */}
            <CustomCard title={forProduct ? 'Search Products' : 'Search Kits'}>
                <div className="flex flex-row gap-4 items-center">
                    <SearchBar
                        searchTerm={searchTerm}
                        onSearch={handleSearchChange}
                        placeholder={forProduct ? 'Search by product name or SKU...' : 'Search by kit name or SKU...'}
                    />
                    {(searchTerm.length !== 0) && (
                        <BiRefresh
                            size={30}
                            className="cursor-pointer text-primary"
                            onClick={resetFilters}
                        />
                    )}
                </div>
            </CustomCard>

            {/* Kit Templates Table */}
            {
                loading ? <div className="flex justify-center items-center h-full">
                    <CustomLoading />
                </div> :
                    <>
                        <CustomCard title={forProduct ? 'Products' : 'Kit Templates'}>
                            <Table
                                headers={[
                                    { label: forProduct ? 'Product Details' : 'Kit Details', key: "kit" },
                                    { label: "Stock & Cost", key: "stock" },
                                    forProduct ? {} : { label: 'Assembly Info', key: "assembly" },
                                    { label: "Actions", key: "actions" }
                                ]}
                                rows={kitTemplates.results?.map((kit) => ({
                                    kit: (
                                        <div>
                                            <p className="font-semibold">{kit.name}</p>
                                            <p className="text-sm text-gray-600">{kit.sku}</p>
                                            <p className="text-xs text-gray-500">{kit.description || <span className="italic text-gray-400">No description</span>}</p>
                                        </div>
                                    ),
                                    stock: (
                                        <div>
                                            <p className="font-semibold mb-2">{forProduct ? 'Total Purchased Stock' : 'Total Kit Produced'} : {kit.quantity}</p>
                                            <p className="font-semibold mb-2 bg-primary/10 text-primary px-2 py-1 rounded-lg w-min">Available Product Stock: {kit.product_stock}</p>
                                            {/* <Dropdown
                                                color={getStatusBadge(kit.status, true)}
                                                options={[
                                                    { label: "Active", value: "active" },
                                                    { label: "Inactive", value: "inactive" },
                                                    { label: "Discontinued", value: "discontinued" }
                                                ]}
                                                value={kit.status}
                                                onChange={(e) => handleStatusChange(e, kit)}
                                            /> */}
                                        </div>
                                    ),
                                    assembly: (
                                        <div>
                                            <p className="text-sm">Components: {kit.kit_components ? kit.kit_components.length : 0}</p>
                                        </div>
                                    ),
                                    actions: (
                                        <div className="flex flex-row gap-2 items-center">
                                            <Button
                                                className="bg-primary hover:bg-primary/40 text-white px-2 py-0.5 text-xs rounded"
                                                style={{ minWidth: 0, fontSize: '11px' }}
                                                onClick={() => openKitDetailsModal(kit)}
                                            >
                                                <History size={12} />
                                            </Button>
                                            <Link
                                                className="text-center hover:bg-blue-600 text-white px-4 py-1.5 text-xs rounded bg-blue-500"
                                                to={`/inventory/kit-assembly/edit`} state={{ kit }}
                                            >
                                                Update Stock
                                            </Link>
                                        </div>
                                    )
                                }))}
                            />
                            <Pagination
                                currentPage={currentPage}
                                totalPages={Math.ceil(kitTemplates?.count / pageSize)}
                                pageSize={pageSize}
                                totalEntries={kitTemplates?.count}
                                onPageChange={handlePageChange}
                                data={kitTemplates}
                                setData={setKitTemplates}
                            />
                        </CustomCard>

                        {
                            !forProduct &&
                            <CustomCard title="Recent Assembly Activity">
                                <Table
                                    headers={[
                                        { label: "Kit", key: "kit" },
                                        { label: "Quantity", key: "quantity" },
                                        { label: "Assembled By", key: "assembler" },
                                        { label: "Date & Time", key: "date" },
                                        { label: "Status", key: "status" }
                                    ]}
                                    rows={assemblyHistory.results?.map((activity) => ({
                                        kit: <p className="font-medium">{activity.kit}</p>,
                                        quantity: <span className="font-semibold">{activity.quantity} units</span>,
                                        assembler: activity.added_by,
                                        date: new Date(activity.created_at).toLocaleString(),
                                        status: (
                                            <span className={getStatusBadge("assembled")}>
                                                Assembled
                                            </span>
                                        )
                                    }))}
                                />
                            </CustomCard>
                        }
                    </>}

            {/* Kit Details Modal */}
            <ShowModal
                show={showKitDetailsModal}
                onClose={() => setShowKitDetailsModal(false)}
                title={forProduct ? 'Product Details' : 'Kit Details'}
                isSidebar={true}
                size="lg"
                componentToPass={<KitAssemblyDetailedView kit={selectedKit} forProduct={forProduct} />}
            />
        </>
    );
};

export default KitAssembly; 